/**
 * `#uc1UnitsChart` — the Overview's per-unit picture, which follows the active chip
 * (2 Oct chart round, design §1.2).
 *
 * * **Priority**: a `UnitMatrix` — one row per unit at the level the pane is on, one
 *   column per constraint in C1…C5 order, a priority dot beside each name. Clicking a
 *   column header presses that constraint's chip, so the matrix is also the way into the
 *   per-constraint reading.
 * * **A constraint**: `DimensionBars` of that constraint's own number per unit, against
 *   the reference the rule compared it with, flagged bars in the constraint's colour. At
 *   country level the number is the share of that country's watersheds flagged, so the
 *   scale is fixed to 0–100 % and there is no reference to draw.
 *
 * A dimension whose rule is `any` of several metrics (soil, rainfall) can answer from a
 * different metric in different units per unit — organic carbon in g/kg here, pH there.
 * One shared bar scale across mixed units makes a small number a sliver beside a large
 * one, so the chart switches to `scale="row"` as soon as the rows disagree about their
 * unit, and each row's note names the metric it answered from and that unit. An `outside
 * lo–hi` rule has no single tick to draw either: the band goes in the note instead.
 *
 * Both read the units rows the choropleth is already painted from — no request of its own
 * (the round's performance rule). A row from a run before `metrics` was served carries no
 * number at all; the chart then says so rather than drawing an empty frame.
 */
import { useTranslation } from 'react-i18next';

import type { IntelUnitRow } from '../../lib/api';
import { useAppStore } from '../../lib/state';
import { DimensionBars, type DimRow } from './charts/DimensionBars';
import { UnitMatrix, type UnitMatrixColumn } from './charts/UnitMatrix';
import {
  CATEGORY_COLORS,
  CATEGORY_RANK,
  CONSTRAINT_COLORS,
  UC1_DIMENSIONS,
  type Uc1Level,
} from './uc1Constants';

export interface Uc1UnitsChartProps {
  rows: IntelUnitRow[];
  /** The active chip — `'priority'` or one of the five constraint ids. */
  chip: string;
  level: Uc1Level;
  /**
   * The focused unit (`#intelFocus`, or a map click). Every unit stays in the chart —
   * dropping the other ten would answer "how does this one compare" with nothing to
   * compare it to — and this one is picked out: its name bold, its bar outlined.
   */
  selectedId?: string | null;
}

/** Priority first, then name — the order a reader scans the matrix in. */
function sortUnits(rows: IntelUnitRow[]): IntelUnitRow[] {
  return [...rows].sort((a, b) => {
    const ra = CATEGORY_RANK[a.category] ?? 9;
    const rb = CATEGORY_RANK[b.category] ?? 9;
    return ra === rb ? a.name.localeCompare(b.name) : ra - rb;
  });
}

export function Uc1UnitsChart({ rows, chip, level, selectedId = null }: Uc1UnitsChartProps) {
  const { t } = useTranslation();
  const setIntelUc1Chip = useAppStore((s) => s.setIntelUc1Chip);
  if (!rows.length) return null;

  const units = sortUnits(rows);
  const isCountry = level === 'admin0';

  if (chip === 'priority') {
    const columns: UnitMatrixColumn[] = UC1_DIMENSIONS.map((id, i) => ({
      id,
      label: t(`intel.uc1.dimension.${id}`, { defaultValue: id }),
      short: `C${i + 1}`,
      color: CONSTRAINT_COLORS[id]!,
    }));
    return (
      <div id="uc1UnitsChart" className="intel-unitschart intel-card" data-testid="uc1-units-matrix">
        <h6 className="intel-card-title">{t('intel.uc1.chart.matrix')}</h6>
        <UnitMatrix
          units={units.map((r) => ({ id: r.unit_id, name: r.name, category: r.category }))}
          columns={columns}
          flagOf={(unitId, columnId) =>
            rows.find((r) => r.unit_id === unitId)?.flags?.[columnId]
          }
          categoryColors={CATEGORY_COLORS}
          categoryLabel={(category) =>
            t(`intel.uc1.category.${category}`, { defaultValue: category })
          }
          statusLabels={{
            flagged: t('intel.uc1.row.status.flagged'),
            notFlagged: t('intel.uc1.row.status.notFlagged'),
            notAssessed: t('intel.uc1.row.status.notAssessed'),
          }}
          ariaLabel={t('intel.uc1.chart.matrix')}
          selectedId={selectedId}
          onSelectColumn={setIntelUc1Chip}
        />
        <ul className="chart-legend" data-testid="uc1-matrix-key">
          {columns.map((column) => (
            <li key={column.id}>
              <span className="swatch" style={{ background: column.color }} />
              <span className="legend-label">{`${column.short} ${column.label}`}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const dimensionLabel = t(`intel.uc1.dimension.${chip}`, { defaultValue: chip });
  const bars: DimRow[] = units.map((row) => {
    const metric = row.metrics?.[chip];
    const flagged = isCountry ? false : row.flags?.[chip] === true;
    const unit = isCountry ? '%' : (metric?.unit ?? '');
    // `outside lo–hi` has two boundaries: no tick, the band in words instead.
    const range = Array.isArray(metric?.reference) ? (metric.reference as number[]) : null;
    // The unit is not repeated here: `DimensionBars` already prints it beside the value,
    // and a note of "· g/kg" after "8.1 g/kg" is the same word twice — on a row whose
    // metric is unnamed (a run that serves no `metrics[dim].metric`) it was the whole note.
    const note = [
      metric?.metric
        ? t(`intel.uc1.metric.${metric.metric}`, { defaultValue: metric.metric })
        : null,
      range && range.length >= 2
        ? t('intel.uc1.row.outside', { lo: range[0], hi: range[1] })
        : null,
    ]
      .filter(Boolean)
      .join(' · ');
    return {
      id: row.unit_id,
      label: row.name,
      value: typeof metric?.value === 'number' ? metric.value : null,
      unit,
      reference: typeof metric?.reference === 'number' ? metric.reference : null,
      flagged,
      ...(note ? { note } : {}),
      ...(flagged ? { color: CONSTRAINT_COLORS[chip] } : {}),
    };
  });

  /**
   * One shared scale only while every row reads in the same unit — which is the usual
   * case, and the one where comparing bar lengths across units means something.
   */
  const unitsSeen = new Set(bars.map((b) => b.unit).filter(Boolean));
  const scale: 'shared' | 'row' = unitsSeen.size > 1 ? 'row' : 'shared';

  if (!bars.some((b) => b.value != null)) {
    return (
      <div id="uc1UnitsChart" className="intel-unitschart intel-card">
        <h6 className="intel-card-title">{dimensionLabel}</h6>
        <p className="note" data-testid="uc1-units-pending">
          {t('intel.uc1.chart.unitsPending')}
        </p>
      </div>
    );
  }

  return (
    <div id="uc1UnitsChart" className="intel-unitschart intel-card" data-testid="uc1-units-bars">
      <h6 className="intel-card-title">
        {isCountry ? t('intel.uc1.chart.unitsShare', { name: dimensionLabel }) : dimensionLabel}
      </h6>
      <DimensionBars
        rows={bars}
        scale={scale}
        selectedId={selectedId}
        {...(isCountry ? { max: 100 } : {})}
        ariaLabel={dimensionLabel}
        referenceLabel={t('intel.chart.reference')}
        refCaption={t('intel.chart.ref')}
      />
    </div>
  );
}

export default Uc1UnitsChart;
